import { useShallow } from 'zustand/react/shallow';
import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { setNavOpen } from '../store/ui';
import { compareSnowflakes, RelationshipType, type Channel } from '@comms/shared';
import { displayName, isUnread, useStore } from '../store';
import { api } from '../lib/api';
import { Avatar, Button, ErrorText, Icon, Modal, cx } from './ui';

export function dmTitle(s: ReturnType<typeof useStore.getState>, c: Channel): string {
  if (c.name) return c.name;
  const others = (c.recipients ?? []).filter((r) => r.id !== s.me?.id);
  return others.map((r) => displayName(s, r.id)).join(', ') || 'Just you';
}

function NewGroupModal({ onClose }: { onClose: () => void }) {
  const friends = useStore(
    useShallow((s) =>
      Object.values(s.relationships).filter((r) => r.type === RelationshipType.FRIEND),
    ),
  );
  const [picked, setPicked] = useState<string[]>([]);
  const [error, setError] = useState<unknown>(null);
  const navigate = useNavigate();
  async function create() {
    try {
      const c = await api<Channel>('POST', '/users/@me/channels', { recipientIds: picked });
      onClose();
      navigate(`/channels/@me/${c.id}`);
      setNavOpen(false);
    } catch (err) {
      setError(err);
    }
  }
  return (
    <Modal title="New message" onClose={onClose}>
      <p className="mb-3 text-sm text-text-2">
        Select up to 9 friends. You can turn on end-to-end encryption from the conversation header.
      </p>
      <div className="max-h-72 space-y-1 overflow-y-auto">
        {friends.length === 0 && <p className="text-sm text-text-3">Add some friends first.</p>}
        {friends.map((f) => (
          <label
            key={f.id}
            className="flex cursor-pointer items-center gap-3 rounded px-2 py-1.5 hover:bg-bg-hover"
          >
            <input
              type="checkbox"
              checked={picked.includes(f.id)}
              onChange={(e) =>
                setPicked(
                  e.target.checked
                    ? [...picked, f.id].slice(0, 9)
                    : picked.filter((p) => p !== f.id),
                )
              }
            />
            <Avatar userId={f.id} size={32} />
            <span className="text-text-1">{f.user.displayName}</span>
            <span className="text-sm text-text-3">{f.user.username}</span>
          </label>
        ))}
      </div>
      <ErrorText error={error} />
      <div className="mt-4 flex justify-end gap-2">
        <Button variant="ghost" onClick={onClose}>
          Cancel
        </Button>
        <Button disabled={picked.length === 0} onClick={create}>
          {picked.length > 1 ? 'Create group' : 'Open DM'}
        </Button>
      </div>
    </Modal>
  );
}

export function DmSidebar({ activeChannelId }: { activeChannelId?: string }) {
  const state = useStore();
  const pending = Object.values(state.relationships).filter(
    (r) => r.type === RelationshipType.INCOMING,
  ).length;
  const [creating, setCreating] = useState(false);
  const dms = Object.values(state.channels)
    .filter((c) => c.guildId === null)
    .sort((a, b) => compareSnowflakes(b.lastMessageId ?? b.id, a.lastMessageId ?? a.id));
  return (
    <div className="flex min-h-0 flex-1 flex-col">
      <div className="flex h-12 items-center border-b border-bg-0 px-3 text-sm text-text-3">
        <Icon name="message" className="mr-2 h-4 w-4" /> Direct messages
      </div>
      <div className="flex-1 overflow-y-auto p-2">
        <Link
          to="/channels/@me"
          className={cx(
            'flex items-center gap-3 rounded px-2 py-2',
            !activeChannelId ? 'bg-bg-active text-text-0' : 'text-text-2 hover:bg-bg-hover',
          )}
        >
          <Icon name="users" /> Friends
          {pending > 0 && (
            <span className="ml-auto rounded-full bg-danger px-1.5 text-xs font-bold text-white">
              {pending}
            </span>
          )}
        </Link>
        <div className="mt-4 flex items-center justify-between px-2 text-xs font-bold uppercase tracking-wide text-text-3">
          Direct messages
          <button
            type="button"
            title="New message"
            onClick={() => setCreating(true)}
            className="hover:text-text-0"
          >
            <Icon name="plus" className="h-4 w-4" />
          </button>
        </div>
        <div className="mt-1 space-y-0.5">
          {dms.map((c) => {
            const others = (c.recipients ?? []).filter((r) => r.id !== state.me?.id);
            const unread = isUnread(state, c.id) && c.id !== activeChannelId;
            return (
              <div
                key={c.id}
                className={cx(
                  'group flex items-center rounded px-2 py-1.5',
                  c.id === activeChannelId
                    ? 'bg-bg-active text-text-0'
                    : 'text-text-3 hover:bg-bg-hover hover:text-text-1',
                )}
              >
                <Link
                  to={`/channels/@me/${c.id}`}
                  className="flex min-w-0 flex-1 items-center gap-3"
                >
                  {others.length === 1 ? (
                    <Avatar userId={others[0]!.id} size={32} showStatus />
                  ) : (
                    <span className="flex h-8 w-8 items-center justify-center rounded-full bg-accent text-white">
                      <Icon name="users" className="h-4 w-4" />
                    </span>
                  )}
                  <span className={cx('min-w-0 flex-1', unread && 'font-semibold text-text-0')}>
                    <span className="flex items-center gap-1">
                      <span className="truncate">{dmTitle(state, c)}</span>
                      {c.e2ee && (
                        <span title="End-to-end encrypted" className="shrink-0 text-success">
                          <Icon name="lock" className="h-3 w-3" />
                        </span>
                      )}
                    </span>
                    {others.length > 1 && (
                      <span className="block text-xs text-text-3">{others.length + 1} members</span>
                    )}
                  </span>
                </Link>
                {unread && <span className="h-2 w-2 rounded-full bg-text-0" />}
                <button
                  type="button"
                  title="Close"
                  onClick={() => void api('DELETE', `/channels/${c.id}`)}
                  className="ml-1 hidden text-text-3 hover:text-text-0 group-hover:block"
                >
                  <Icon name="x" className="h-4 w-4" />
                </button>
              </div>
            );
          })}
        </div>
      </div>
      {creating && <NewGroupModal onClose={() => setCreating(false)} />}
    </div>
  );
}
